import { Injectable, effect, inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { type Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';

import { ApiClient } from '@core/api/api-client';
import { AuthSession } from '@core/auth/auth-session';
import { type MyPermissions, UserPermissions } from '@core/auth/user-permissions';

export const MY_PERMISSIONS_PATH = 'back-office/me/permissions';
export const FINGERPRINT_HEADER = 'X-Authz-Fingerprint';

/** Catches a change made while this tab sat idle and sent no requests. */
export const PERMISSIONS_POLL_MS = 60_000;

/**
 * Loads the operator's permissions and refetches them when they change. Three signals,
 * because each covers what the others miss:
 * - a new `X-Authz-Fingerprint` on any back-office response (an active operator),
 * - a poll every minute (an idle tab; the endpoint sends an ETag, so the browser
 *   revalidates instead of downloading),
 * - a 403 `PERMISSION_DENIED` (the backstop).
 */
@Injectable({ providedIn: 'root' })
export class PermissionsSync {
  private readonly api = inject(ApiClient);
  private readonly permissions = inject(UserPermissions);
  private readonly session = inject(AuthSession);

  private inFlight: Observable<boolean> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | undefined;

  constructor() {
    // Signing out drops the old operator's permissions.
    effect(() => {
      if (!this.session.isAuthenticated()) {
        this.stop();
      }
    });
  }

  /**
   * Fetches the permissions now and starts the poll. Never fails: on an error the last
   * known list is kept (or none at all), and the next poll tries again.
   * Emits whether the fetch succeeded.
   */
  load(): Observable<boolean> {
    this.startPolling();
    this.inFlight ??= this.api.get<MyPermissions>(MY_PERMISSIONS_PATH).pipe(
      tap((permissions) => {
        // An answer arriving after sign-out belongs to the old session.
        if (this.session.isAuthenticated()) {
          this.permissions.set(permissions);
        }
      }),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => (this.inFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight;
  }

  /** Called with the fingerprint header of every back-office response. */
  noteFingerprint(fingerprint: string | null): void {
    const known = this.permissions.fingerprint();
    // Before the first load there is nothing to compare with; that load is coming anyway.
    if (fingerprint && known && fingerprint !== known) {
      this.refresh();
    }
  }

  /** Refetches in the background, e.g. after a 403 `PERMISSION_DENIED`. */
  refresh(): void {
    if (this.session.isAuthenticated()) {
      this.load().subscribe();
    }
  }

  private startPolling(): void {
    this.pollTimer ??= setInterval(() => this.refresh(), PERMISSIONS_POLL_MS);
  }

  private stop(): void {
    clearInterval(this.pollTimer);
    this.pollTimer = undefined;
    this.permissions.clear();
  }
}

/** Loads the permissions before the main layout renders, so the menu appears complete. */
export const permissionsResolver: ResolveFn<boolean> = () => inject(PermissionsSync).load();
